import React, { useMemo, useState } from 'react'
import { Box, Button, Typography, makeStyles, Theme } from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import AddIcon from '@material-ui/icons/Add'

import { ConfigureWorkbookSectionModal } from '../PipelineFlowDemo/ConfigureWorkbookSectionModal'
import { POLICY_LOCKED_QUALIFYING_FIELD_IDS } from './conditionalApprovals'
import {
    MAX_CUSTOM_SECTIONS,
    WorkbookBucketId,
    WorkbookSectionDraft,
    WorkbookSectionsState,
    addCustomSection,
    countCustomSections,
    getWorkbookSectionDraft,
    isWorkbookSectionConfigured,
    listWorkbookSections,
    removeCustomSection,
    upsertWorkbookSection,
} from '../PipelineFlowDemo/workbookSectionDraft'
import { platformBucketSummary } from '../PipelineFlowDemo/workbookBuckets'
import type { FieldRegistryAdapter } from '../PipelineFlowDemo/orgFieldRegistry'
import {
    PetsPolicyDraft,
    allowedAnimalTypeOptions,
    allowedDogBreedOptions,
} from './petPolicy'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        height: '100%',
        minHeight: 0,
        overflow: 'auto',
        padding: theme.spacing(0.5, 0, 1),
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        width: '100%',
    },
    card: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
    },
    cardMain: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.25),
        minWidth: 0,
    },
    statusIconOk: {
        color: '#2e7d32',
        fontSize: 22,
        marginTop: 2,
        flexShrink: 0,
    },
    statusIconMissing: {
        color: theme.palette.text.disabled,
        fontSize: 22,
        marginTop: 2,
        flexShrink: 0,
    },
    cardTitle: {
        fontWeight: 600,
        fontSize: '0.9375rem',
    },
    cardSubtitle: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    cardActions: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: theme.spacing(0.5),
        flexShrink: 0,
    },
    configureBtn: {
        textTransform: 'none',
        fontWeight: 600,
    },
    addSectionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(0.5),
    },
    addHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
}))

function sectionSummaryText(draft: {
    kind: string
    bucket_id: string
    summary?: string
}): string {
    if (draft.summary?.trim()) return draft.summary.trim()
    if (draft.kind === 'platform') {
        return platformBucketSummary(draft.bucket_id)
    }
    return ''
}

export const ApplicantDataTab: React.FC<{
    sections: WorkbookSectionsState | undefined
    onSectionsChange: (next: WorkbookSectionsState) => void
    readOnly?: boolean
    fieldRegistry?: FieldRegistryAdapter
    editSectionMeta?: boolean
    /** EZOS admin: unlock catalog field ID / question / type. */
    editCatalogFields?: boolean
    /** Field IDs locked by Qualifying Methods (options / semantics). */
    policyLockedFieldIds?: string[]
    /** Current Property Data pet policy, when a pack is selected. */
    petsPolicy?: PetsPolicyDraft
}> = ({
    sections,
    onSectionsChange,
    readOnly = false,
    fieldRegistry,
    editSectionMeta = false,
    editCatalogFields = false,
    policyLockedFieldIds = POLICY_LOCKED_QUALIFYING_FIELD_IDS,
    petsPolicy,
}) => {
    const classes = useStyles()
    const [modalBucket, setModalBucket] = useState<WorkbookBucketId | null>(
        null,
    )

    const sectionList = useMemo(() => listWorkbookSections(sections), [
        sections,
    ])

    const customCount = countCustomSections(sections)

    const modalDraft = useMemo(
        () =>
            modalBucket ? getWorkbookSectionDraft(sections, modalBucket) : null,
        [modalBucket, sections],
    )

    const animalTypeOptions = useMemo(
        () => (petsPolicy ? allowedAnimalTypeOptions(petsPolicy) : []),
        [petsPolicy],
    )
    const dogBreedOptions = useMemo(
        () => (petsPolicy ? allowedDogBreedOptions(petsPolicy) : []),
        [petsPolicy],
    )

    return (
        <Box className={classes.root}>
            {!readOnly ? (
                <Box className={classes.addSectionRow}>
                    <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        disabled={customCount >= MAX_CUSTOM_SECTIONS}
                        onClick={() => {
                            const next = addCustomSection(
                                sections,
                                `Custom section ${customCount + 1}`,
                            )
                            if (next) {
                                onSectionsChange(next)
                                const added = listWorkbookSections(next).find(
                                    (s) =>
                                        s.kind === 'custom' &&
                                        !sections?.[s.bucket_id],
                                )
                                if (added) setModalBucket(added.bucket_id)
                            }
                        }}
                    >
                        Add custom section
                    </Button>
                    <Typography className={classes.addHint}>
                        {customCount}/{MAX_CUSTOM_SECTIONS} custom sections
                    </Typography>
                </Box>
            ) : null}

            <Box className={classes.list}>
                {sectionList.map((draft) => {
                    const configured = isWorkbookSectionConfigured(
                        sections,
                        draft.bucket_id,
                    )
                    const summary = sectionSummaryText(draft)
                    return (
                        <Box key={draft.bucket_id} className={classes.card}>
                            <Box className={classes.cardMain}>
                                {configured ? (
                                    <CheckCircleOutlineIcon
                                        className={classes.statusIconOk}
                                    />
                                ) : (
                                    <RadioButtonUncheckedIcon
                                        className={classes.statusIconMissing}
                                    />
                                )}
                                <Box>
                                    <Typography className={classes.cardTitle}>
                                        {draft.label}
                                        {draft.kind === 'custom'
                                            ? ' (custom)'
                                            : ''}
                                    </Typography>
                                    {summary ? (
                                        <Typography
                                            className={classes.cardSubtitle}
                                        >
                                            {summary}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Box>
                            <Box className={classes.cardActions}>
                                <Button
                                    className={classes.configureBtn}
                                    color="primary"
                                    variant={
                                        configured ? 'outlined' : 'contained'
                                    }
                                    size="small"
                                    disabled={readOnly}
                                    onClick={() =>
                                        setModalBucket(draft.bucket_id)
                                    }
                                >
                                    {configured ? 'Edit' : 'Configure'}
                                </Button>
                                {draft.kind === 'custom' && !readOnly ? (
                                    <Button
                                        size="small"
                                        color="secondary"
                                        onClick={() =>
                                            onSectionsChange(
                                                removeCustomSection(
                                                    sections,
                                                    draft.bucket_id,
                                                ),
                                            )
                                        }
                                    >
                                        Remove
                                    </Button>
                                ) : null}
                            </Box>
                        </Box>
                    )
                })}
            </Box>

            <ConfigureWorkbookSectionModal
                open={modalBucket != null}
                bucketId={modalBucket}
                sections={sections}
                initialDraft={modalDraft}
                onClose={() => setModalBucket(null)}
                onSave={(draft: WorkbookSectionDraft) => {
                    onSectionsChange(upsertWorkbookSection(sections, draft))
                    setModalBucket(null)
                }}
                onMoveField={(field, toSectionId, fromDraftWithoutField) => {
                    const to = getWorkbookSectionDraft(sections, toSectionId)
                    onSectionsChange({
                        ...(sections ?? {}),
                        [fromDraftWithoutField.bucket_id]: fromDraftWithoutField,
                        [toSectionId]: {
                            ...to,
                            fields: [...to.fields, { ...field }],
                        },
                    })
                }}
                fieldRegistry={fieldRegistry}
                editSectionMeta={editSectionMeta}
                editCatalogFields={editCatalogFields}
                policyLockedFieldIds={policyLockedFieldIds}
                animalTypeOptions={animalTypeOptions}
                dogBreedOptions={dogBreedOptions}
            />
        </Box>
    )
}
